import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Group, NumberInput, Stack, Switch, Text } from "@mantine/core";
import { toast } from "sonner";
import { bookingApprovalsApi, type ApprovalSettings, type ApprovalSettingsInput } from "@/lib/api/booking-approvals";
import { APPROVAL_SETTINGS_KEY, useApprovalSettings } from "@/hooks/use-booking-approvals";
import { decisionError, settingsSourceText } from "@/lib/booking-approvals";

export interface ApprovalSettingsViewProps {
  settings: ApprovalSettings;
  saving: boolean;
  onSave: (input: ApprovalSettingsInput) => void;
}

/** The company's own booking-approval rule (#401 §10) — presentational. */
export function ApprovalSettingsView({ settings, saving, onSave }: ApprovalSettingsViewProps) {
  const [required, setRequired] = useState(settings.requires_approval);
  const [allowance, setAllowance] = useState<number | string>(Number(settings.monthly_allowance_sek));
  useEffect(() => {
    setRequired(settings.requires_approval);
    setAllowance(Number(settings.monthly_allowance_sek));
  }, [settings]);
  const hasOverride = settings.override_requires_approval !== null || settings.override_monthly_allowance_sek !== null;

  return (
    <Card withBorder padding="lg" data-testid="approval-settings">
      <Stack gap="sm">
        <Text fw={500}>Meeting-room approvals</Text>
        <Text fz="sm" c="dimmed">
          When a colleague who isn't a company admin books a room your company pays for, an admin approves it
          first. The room is held for them meanwhile.
        </Text>
        <Switch
          label="Paid bookings need a company admin's approval"
          description={settingsSourceText(settings.requires_approval_source)}
          checked={required}
          disabled={!settings.can_edit || saving}
          onChange={(e) => setRequired(e.currentTarget.checked)}
        />
        <NumberInput
          label="Monthly allowance per person (kr excl. VAT)"
          description={`Bookings within it go straight through; 0 = none. ${settingsSourceText(settings.allowance_source)}`}
          min={0}
          decimalScale={2}
          value={allowance}
          disabled={!settings.can_edit || saving || !required}
          onChange={setAllowance}
        />
        {settings.can_edit && (
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              disabled={saving || !hasOverride}
              onClick={() => onSave({ requires_approval: null, monthly_allowance_sek: null })}
            >
              Use the default
            </Button>
            <Button
              loading={saving}
              onClick={() => onSave({ requires_approval: required, monthly_allowance_sek: String(Number(allowance) || 0) })}
            >
              Save
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

/** Settings page card for tenant admins; also the Company setup guide's
 *  approval step (#436), where `onSaved` ticks the step. */
export function ApprovalSettingsCard({ onSaved }: { onSaved?: () => void } = {}) {
  const qc = useQueryClient();
  const { data } = useApprovalSettings();
  const save = useMutation({
    mutationFn: (input: ApprovalSettingsInput) => bookingApprovalsApi.putSettings(input).then((r) => r.data),
    onSuccess: (out) => {
      qc.setQueryData(APPROVAL_SETTINGS_KEY, out);
      toast.success("Approval settings saved");
      onSaved?.();
    },
    onError: (err) => toast.error(decisionError(err)),
  });
  // Before the switch (MR E) approval does not exist for members yet: show nothing (plan R1/R2).
  if (!data?.enabled) return null;
  return <ApprovalSettingsView settings={data} saving={save.isPending} onSave={(input) => save.mutate(input)} />;
}
